// Training area (#/friends/training): two sides of up to 5 creatures, each a
// copy of one of your creatures or a made-up one, fought with the ranked
// arena's rules and engine. Nothing is saved and nothing is paid: the result
// only shows here. One battle every 5 minutes per account (server-checked).
import { arenaPower } from '../shared/power.ts';
import { arenaFighter, arenaLevel, type ArenaCreature, type BattleResult } from '../shared/arena.ts';
import { SPECIES } from '../shared/species.ts';
import { TRAITS, TRAIT_BY_ID, speciesTrait } from '../shared/traits.ts';
import { MAX_STARS, relicName } from '../shared/items.ts';
import { STAT_NAMES } from '../shared/rolls.ts';
import { CONFIG } from '../shared/config.ts';
import { SOCIAL, asFighter, checkTestSpec, testCreature, type TestSpec, type TrainSlot } from '../shared/social.ts';
import type { ClientMsg, CreatureDTO } from '../shared/protocol.ts';
import { S, serverNow } from './state.ts';
import { spriteUrl } from './assets.ts';
import { esc, full, cname, left } from './fmt.ts';
import { battleLogHtml } from './battlelog.ts';
import { synergyChips } from './synergy.ts';
import { F } from './social.ts';
import { heldChip } from './held.ts';
import { BOARD, type BoardRow } from '../shared/board.ts';
import { boardOn, frontLabel, reachBadge, rowToggle, shownRows } from './rows.ts';
import { boardPrep, type PrepUnit } from './boardview.ts';
import { openTeamBuilder } from './teambuilder.ts';
import { psecTabs, psecWrap, openSection } from './psec.ts';

export const T = {
  sides: [[], []] as [TrainSlot[], TrainSlot[]],
  /** The made-up creature being edited: its side, its place (-1 = a new one) and the fields. */
  edit: null as null | { side: 0 | 1; i: number; spec: TestSpec },
  /** Which side the copy picker adds to, per side: the picked creature id. */
  pick: [0, 0] as [number, number],
  result: null as BattleResult | null,
  at: 0,
  /** The side being placed on the board. */
  place: 0 as 0 | 1,
};

/** A slot's id on the board (slots have no creature id of their own; made-up ones have none at all). */
const slotId = (side: 0 | 1, i: number) => 900_000 + side * 10 + i;

let send: (m: ClientMsg) => void = () => {};
let rerender: () => void = () => {};
export function bindTraining(o: { send: (m: ClientMsg) => void; render: () => void }) { send = o.send; rerender = o.render; }

export function onTraining(m: { result: BattleResult; at: number }) { T.result = m.result; T.at = m.at; openSection('tr', 'res'); rerender(); }

const SIDE = ['Side A', 'Side B'] as const;
const freshSpec = (): TestSpec => ({ species: 'snowpup', level: 100, aura: false, bloodline: 0, quality: 1, ivs: [15, 15, 15, 15, 15, 15], trait: 'sturdy', stars: 0, refine: [0, 0, 0, 0, 0] });
/** The traits a species can show: every trait but the element traits of elements it lacks (shared/traits.ts ownTrait). */
const traitChoices = (species: string) => TRAITS.filter((t) => speciesTrait(t.id, species) === t.id);
/** A made-up copy of one of your creatures, ready to change (its relic comes along). */
const specOf = (c: CreatureDTO): TestSpec => ({
  species: c.species, level: c.level, aura: c.rolls.aura, bloodline: c.rolls.bloodline, quality: c.rolls.quality, ivs: [...c.rolls.ivs], trait: speciesTrait(c.rolls.trait, c.species),
  stars: c.stars, refine: [...(c.refine ?? [0, 0, 0, 0, 0])], ...(c.relic ? { relicOf: c.id } : {}),
});

/** A slot as it will fight, or null when its creature is gone. */
function fighterOf(slot: TrainSlot): ArenaCreature | null {
  if ('own' in slot) { const c = S.creatures.get(slot.own); return c && !c.listed ? asFighter(c) : null; }
  const chk = checkTestSpec(slot.test);
  if (!chk.ok) return null;
  const relic = chk.spec.relicOf !== undefined ? S.creatures.get(chk.spec.relicOf)?.relic ?? null : null;
  return testCreature(chk.spec, 1, relic);
}

/**
 * The rows a side stands on, per slot (the board): the rows set on its slots (all or none; a slot added later shows
 * at the back), else defaultRows. A slot whose creature is gone shows at the back.
 */
function sideRowsOf(side: 0 | 1): BoardRow[] {
  const slots = T.sides[side];
  const team = slots.map((x, i) => { const f = fighterOf(x); return f ? { ...f, id: i + 1 } : null; });
  const ok = team.filter((c): c is ArenaCreature => !!c);
  const set = slots.some((x) => x.row);
  const shown = shownRows(ok, set ? Object.fromEntries(slots.map((x, i) => [i + 1, x.row ?? 'back'])) : null);
  return team.map((c) => (c ? shown[ok.indexOf(c)] : 'back'));
}

function statsLine(f: ArenaCreature) {
  const st = arenaFighter(f).stats;
  return `fights as Lv ${arenaLevel(f.level)} · HP ${full(st.hp)} · Atk ${full(st.might)} · Def ${full(st.guard)} · Mag ${full(st.focus)} · Res ${full(st.will)} · Spd ${full(st.swift)} · Power (Arena) ${full(arenaPower(f))}`;
}

function slotHtml(side: 0 | 1, slot: TrainSlot, i: number, n: number, rows: BoardRow[]) {
  const f = fighterOf(slot);
  const full3 = rows.filter((r) => r === 'front').length >= BOARD.frontMax;
  const rowEd = boardOn() && f ? `<span class="rw-ed">${reachBadge(f.species)}</span>` : '';
  const own = 'own' in slot ? S.creatures.get(slot.own) : undefined;
  const name = f ? esc(SPECIES[f.species]?.name ?? f.species) : 'Missing creature';
  const kind = 'own' in slot ? '<span class="pill">Copy</span>' : '<span class="pill lead">Made up</span>';
  const extra = 'test' in slot ? [slot.test.bloodline ? `Bloodline ${CONFIG.bloodline[slot.test.bloodline]?.name}` : '', slot.test.stars ? `${slot.test.stars}★` : '', slot.test.refine.some((x) => x > 0) ? `refine +${slot.test.refine.reduce((a, b) => a + b, 0)}` : '', slot.test.relicOf !== undefined ? 'with relic' : ''].filter(Boolean).join(' · ') : own?.relic ? esc(relicName(own.relic)) : '';
  return `<li data-key="s${side}-${i}"><img src="${f ? spriteUrl(f.species, f.rolls.aura) : ''}" alt=""><div class="grow"><div><b>${i + 1}. ${name}</b> <span class="muted">Lv ${f ? full(f.level) : '?'}</span> ${kind}${f?.rolls.aura ? ' <span class="aura-tag">✦ Aura</span>' : ''}${own ? heldChip(own) : ''}</div>
    ${extra ? `<div class="muted">${extra}</div>` : ''}${f ? `<div class="muted tr-stats">${statsLine(f)}</div>` : ''}</div>
    <div class="tr-acts">${rowEd}<button class="btn tiny" data-act="tr-up" data-side="${side}" data-id="${i}" aria-label="Move up"${i ? '' : ' disabled'}>↑</button><button class="btn tiny" data-act="tr-down" data-side="${side}" data-id="${i}" aria-label="Move down"${i < n - 1 ? '' : ' disabled'}>↓</button>
      <button class="btn tiny" data-act="tr-edit" data-side="${side}" data-id="${i}">${'own' in slot ? 'Change as made-up' : 'Edit'}</button><button class="btn tiny" data-act="tr-remove" data-side="${side}" data-id="${i}" aria-label="Remove">✕</button></div></li>`;
}

function sideHtml(side: 0 | 1) {
  const slots = T.sides[side];
  const full5 = slots.length >= SOCIAL.trainingSide;
  const rows = sideRowsOf(side);
  return `<section class="panel tr-side"><div class="row between"><h4>${SIDE[side]} <span class="muted">${slots.length} of ${SOCIAL.trainingSide}</span></h4>${slots.length ? `${boardOn() ? '' : frontLabel(rows)}<button class="btn tiny" data-act="tr-clear" data-side="${side}">Clear</button>` : ''}</div>
    <ol class="tr-list" data-fit="tr-side-${side}">${slots.map((s, i) => slotHtml(side, s, i, slots.length, rows)).join('') || '<li class="hint">Empty</li>'}</ol>
    ${synergyChips(slots.map(fighterOf).filter((c): c is ArenaCreature => !!c))}
    <div class="actions"><button class="btn small" data-act="tr-build" data-side="${side}">Change team</button>
      ${full5 ? '' : `<button class="btn small" data-act="tr-add-test" data-side="${side}">Add made-up creature</button>`}
      <button class="btn small" data-act="tr-team" data-side="${side}">Copy hunt team</button></div></section>`;
}

const num = (k: string, v: number, label: string, min: number, max: number, step = 1) => `<label><span>${label}</span><input type="number" data-tr="${k}" min="${min}" max="${max}" step="${step}" value="${v}" inputmode="decimal"></label>`;

function editorHtml() {
  const e = T.edit!;
  const s = e.spec;
  const species = Object.values(SPECIES).sort((a, b) => a.name.localeCompare(b.name));
  const relics = [...S.creatures.values()].filter((c) => c.relic);
  const preview = (() => { const chk = checkTestSpec(s); if (!chk.ok) return ''; const relic = s.relicOf !== undefined ? S.creatures.get(s.relicOf)?.relic ?? null : null; return statsLine(testCreature(chk.spec, 1, relic)); })();
  return `<section class="panel tr-edit"><h4>${e.i < 0 ? 'New made-up creature' : 'Edit made-up creature'} · ${SIDE[e.side]}</h4>
    <div class="tr-grid">
      <label class="wide"><span>Species</span><select data-tr="species">${species.map((sp) => `<option value="${sp.id}"${sp.id === s.species ? ' selected' : ''}>${esc(sp.name)} (${sp.rarity}, stage ${sp.stage})</option>`).join('')}</select></label>
      ${num('level', s.level, 'Level', 1, SOCIAL.testLevelMax)}
      <label><span>Bloodline</span><select data-tr="bloodline">${CONFIG.bloodline.map((b, i) => `<option value="${i}"${i === s.bloodline ? ' selected' : ''}>${esc(b.name)} (+${Math.round((b.mult - 1) * 100)}%)</option>`).join('')}</select></label>
      ${num('quality', s.quality, `Quality (×${CONFIG.quality.min} to ×${CONFIG.quality.max})`, CONFIG.quality.min, CONFIG.quality.max, 0.001)}
      <label><span>Stars</span><select data-tr="stars">${Array.from({ length: MAX_STARS + 1 }, (_, i) => `<option value="${i}"${i === s.stars ? ' selected' : ''}>${i}</option>`).join('')}</select></label>
      <label><span>Trait</span><select data-tr="trait">${traitChoices(s.species).map((t) => `<option value="${t.id}"${t.id === speciesTrait(s.trait, s.species) ? ' selected' : ''}>${esc(t.name)}: ${esc(t.desc)}</option>`).join('')}</select></label>
      <label class="tr-check"><input type="checkbox" data-tr="aura"${s.aura ? ' checked' : ''}> <span>Aura (×${CONFIG.auraMult} all stats)</span></label>
      <label class="wide"><span>Relic</span><select data-tr="relicOf"><option value="">No relic</option>${relics.map((c) => `<option value="${c.id}"${s.relicOf === c.id ? ' selected' : ''}>${esc(relicName(c.relic!))} (on your ${esc(cname(c))})</option>`).join('')}</select></label>
    </div>
    <div class="tr-sub">Talents (0-31)</div><div class="tr-six">${STAT_NAMES.map((n, i) => num(`iv${i}`, s.ivs[i], n, 0, 31)).join('')}</div>
    <div class="tr-sub">Refine</div><div class="tr-six">${STAT_NAMES.slice(0, 5).map((n, i) => num(`rf${i}`, s.refine[i], n, 0, SOCIAL.testRefineMax)).join('')}</div>
    ${preview ? `<p class="muted tr-stats">${preview}</p>` : ''}
    <div class="actions"><button class="btn small primary" data-act="tr-save">${e.i < 0 ? 'Add to' : 'Save on'} ${SIDE[e.side]}</button><button class="btn small" data-act="tr-cancel">Cancel</button></div></section>`;
}

/** Both sides on the board; the side being placed is yours to drag, the other stands opposite. */
function boardHtml(): string {
  if (!boardOn() || !T.sides.some((x) => x.length)) return '';
  const units = (side: 0 | 1): PrepUnit[] => {
    const rows = sideRowsOf(side);
    return T.sides[side].flatMap((slot, i) => {
      const f = fighterOf(slot);
      const own = 'own' in slot ? S.creatures.get(slot.own) : undefined;
      return f ? [{ id: slotId(side, i), species: f.species, level: f.level, aura: f.rolls.aura, row: rows[i], held: own?.held ?? null }] : [];
    });
  };
  const me = T.place, other = (1 - me) as 0 | 1;
  const seg = `<div class="seg tr-place" role="group" aria-label="Side to place">${([0, 1] as const).map((x) => `<button class="${x === me ? 'on' : ''}" data-act="tr-place" data-side="${x}" aria-pressed="${x === me}">${SIDE[x]}</button>`).join('')}</div>`;
  return `<section class="panel tr-board">${seg}${boardPrep({
    key: `training:${me}`,
    mine: units(me),
    theirs: units(other),
    labels: [SIDE[me], SIDE[other]],
    scene: 'plain',
    onRows: (picks) => {
      const rows = sideRowsOf(me);
      T.sides[me].forEach((x, i) => { x.row = picks[slotId(me, i)] ?? rows[i]; });
      rerender();
    },
  })}</section>`;
}

export function trainingPane(): string {
  const now = serverNow();
  const at = F.data?.trainingAt ?? 0;
  const wait = at > now ? at - now : 0;
  const ready = T.sides[0].length > 0 && T.sides[1].length > 0 && T.sides.every((s) => s.every((x) => fighterOf(x)));
  const result = T.result ? `<section class="panel tr-result"><div class="row between"><h4>Result</h4><span class="row"><span class="muted">${new Date(T.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · not saved</span><button class="btn tiny tr-back" data-act="psec" data-id="tr:a">Teams</button></span></div>${battleLogHtml(T.result, [SIDE[0], SIDE[1]], 0)}</section>` : '';
  // The editor takes the whole page; otherwise phones and tablets show one part at a time under tabs.
  if (T.edit) return `<div class="tr-page editing">${editorHtml()}</div>`;
  const board = boardHtml();
  const secs = [...(board ? [{ key: 'board', title: 'Board' }] : []), { key: 'a', title: `${SIDE[0]} ${T.sides[0].length}/${SOCIAL.trainingSide}` }, { key: 'b', title: `${SIDE[1]} ${T.sides[1].length}/${SOCIAL.trainingSide}` }, ...(result ? [{ key: 'res', title: 'Result' }] : [])];
  const keys = secs.map((x) => x.key);
  const sec = (key: string, html: string) => psecWrap('tr', key, keys, html);
  return `<div class="tr-page">${psecTabs('tr', secs)}
    ${board ? sec('board', board) : ''}
    <div class="tr-cols">${sec('a', sideHtml(0))}${sec('b', sideHtml(1))}</div>
    <div class="actions tr-go"><button class="btn primary" data-act="tr-fight"${ready && !wait ? '' : ' disabled'}>Fight</button>
      <span class="muted">${wait ? `Next training battle in <span class="cd">${left(wait)}</span>` : ready ? 'Ready.' : ''}</span></div>
    ${result ? sec('res', result) : ''}</div>`;
}

export function trainingClick(el: HTMLElement): boolean {
  const act = el.dataset.act ?? '';
  if (!act.startsWith('tr-')) return false;
  const side = (Number(el.dataset.side) === 1 ? 1 : 0) as 0 | 1;
  const i = Number(el.dataset.id);
  const list = T.sides[side];
  switch (act) {
    case 'tr-add-own': {
      const sel = document.querySelector(`[data-tr-pick="${side}"]`) as HTMLSelectElement | null;
      const id = Number(sel?.value) || T.pick[side];
      if (id && list.length < SOCIAL.trainingSide) list.push({ own: id });
      break;
    }
    case 'tr-add-test': T.edit = { side, i: -1, spec: freshSpec() }; break;
    case 'tr-place': T.place = side; break;
    case 'tr-build': {
      // Copies of your creatures come from the team builder; made-up ones stay after them.
      const own = list.flatMap((x) => ('own' in x ? [x.own] : []));
      const made = list.filter((x) => 'test' in x);
      openTeamBuilder({
        title: `${SIDE[side]}: your creatures`, ids: own, max: Math.max(1, SOCIAL.trainingSide - made.length), name: 'training', key: '',
        ok: (c) => !c.listed, asFought: asFighter,
        onSave: (ids) => { T.sides[side] = [...ids.map((x) => ({ own: x })), ...made].slice(0, SOCIAL.trainingSide); rerender(); },
      });
      return true;
    }
    case 'tr-team': {
      const ids = (S.trainer?.team ?? []).filter((id) => S.creatures.get(id) && !S.creatures.get(id)!.listed);
      T.sides[side] = ids.slice(0, SOCIAL.trainingSide).map((own) => ({ own }));
      break;
    }
    case 'tr-clear': T.sides[side] = []; break;
    case 'tr-up': case 'tr-down': { const j = act === 'tr-up' ? i - 1 : i + 1; if (j >= 0 && j < list.length) [list[i], list[j]] = [list[j], list[i]]; break; }
    case 'tr-remove': list.splice(i, 1); break;
    case 'tr-edit': {
      const s = list[i];
      if (!s) break;
      if ('test' in s) T.edit = { side, i, spec: { ...s.test, ivs: [...s.test.ivs], refine: [...s.test.refine] } };
      else { const c = S.creatures.get(s.own); if (c) T.edit = { side, i, spec: specOf(c) }; }
      break;
    }
    case 'tr-cancel': T.edit = null; break;
    case 'tr-save': {
      const e = T.edit;
      if (!e) break;
      const chk = checkTestSpec(e.spec);
      if (!chk.ok) break;
      const slot: TrainSlot = { test: chk.spec };
      if (e.i < 0) { if (T.sides[e.side].length < SOCIAL.trainingSide) T.sides[e.side].push(slot); } else { const row = T.sides[e.side][e.i]?.row; T.sides[e.side][e.i] = row ? { ...slot, row } : slot; }
      T.edit = null;
      break;
    }
    case 'tr-row': {
      const [k, row] = (el.dataset.id ?? '').split(':');
      const at = Number(k);
      if (!list[at] || (row !== 'front' && row !== 'back')) break;
      const rows = sideRowsOf(side);
      if (row === 'front' && rows[at] !== 'front' && rows.filter((r) => r === 'front').length >= BOARD.frontMax) break;
      list.forEach((x, j) => { x.row = j === at ? row : rows[j]; });
      break;
    }
    case 'tr-fight': {
      // Rows go on every slot or none: once any is set, each slot carries the row it shows.
      const out = ([0, 1] as const).map((sd) => { const rows = sideRowsOf(sd); const set = T.sides[sd].some((x) => x.row); return T.sides[sd].map((x, j) => (set ? { ...x, row: rows[j] } : x)); });
      send({ t: 'social', op: { k: 'train', sides: [out[0], out[1]] } }); return true;
    }
    default: return false;
  }
  rerender();
  return true;
}

/** Editor fields and the copy pickers. Returns true when the page should redraw (on change, not on each key). */
export function trainingInput(el: HTMLInputElement | HTMLSelectElement): boolean {
  if (el.dataset.trPick !== undefined) { T.pick[Number(el.dataset.trPick) === 1 ? 1 : 0] = Number(el.value); return false; }
  const k = el.dataset.tr;
  if (!k || !T.edit) return false;
  const s = T.edit.spec;
  const v = el.value;
  // An element trait counts for the species' own element (shared/traits.ts ownTrait), so the spec keeps it that way.
  if (k === 'species') { s.species = v; s.trait = speciesTrait(s.trait, v); }
  else if (k === 'trait') s.trait = TRAIT_BY_ID[v] ? speciesTrait(v, s.species) : s.trait;
  else if (k === 'aura') s.aura = (el as HTMLInputElement).checked;
  else if (k === 'relicOf') { if (v) s.relicOf = Number(v); else delete s.relicOf; }
  else if (k === 'level') s.level = Number(v) || 1;
  else if (k === 'bloodline') s.bloodline = Number(v) || 0;
  else if (k === 'quality') s.quality = Number(v) || 1;
  else if (k === 'stars') s.stars = Number(v) || 0;
  else if (k.startsWith('iv')) s.ivs[Number(k.slice(2))] = Number(v) || 0;
  else if (k.startsWith('rf')) s.refine[Number(k.slice(2))] = Number(v) || 0;
  else return false;
  return true;
}
